<script lang="ts">
  import { onMount } from 'svelte';
  import { setupProjectAnimations, animateValue } from '$lib/utils/animations';
  import LineChart from '$lib/components/LineChart.svelte';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import FeatureCell from '$lib/components/FeatureCell.svelte';
  import IndustryTag from '$lib/components/IndustryTag.svelte';
  import ProjectHeroSplit from '$lib/components/ProjectHeroSplit.svelte';
  
  let scrollY = 0;
  let translateY = 0;
  
  // Make the parallax effect reactive
  $: translateY = scrollY * 0.5;

  let titleContent: HTMLElement;

  // Mock data for the charts
  const infectionData = [4, 2, 5, 3, 7, 6, 8, 4, 3];
  const severityData = [6, 5, 7, 4, 3, 2, 4, 3, 2];
  const capacityData = [8, 7, 8, 8, 7, 6, 7, 8, 7];

  onMount(() => {
    // Setup scroll animations
    setupProjectAnimations();
  });
</script>


<svelte:window bind:scrollY />

<div class="min-h-screen bg-white">
  <ProjectHeroSplit
    title="Infektionsradar"
    client="Federal Ministry of Health"
    clientLogo="https://upload.wikimedia.org/wikipedia/commons/thumb/9/9f/BMG_Logo.svg/400px-BMG_Logo.svg.png"
    industry="Healthcare"
    product="Data-driven dashboard for pandemic monitoring"
    heroMedia={{
      type: 'image',
      src: "pandemieradar/public-health-light.jpg"
    }}
    metrics={[
      { value: 120000, label: "Visits per Week" },
      { value: 45, label: "Data Updates per Week" },
      { value: 4, label: "Languages" }
    ]}
    roles='<strong>Creative Lead</strong><br />Shaping product narrative, UX, and data-driven storytelling'
  />

  <!-- Content -->
  <div class="project-section">
    <!-- Large Text Block -->
    <div class="space-y-2 mb-12 animate-on-view border-t border-b border-black/10 py-6" data-delay="1">
      <p class="large-text text-black/90 animate-on-view">
        Pandemieradar is a real-time healthcare communication platform supporting public health decisions with data-driven insights.
      </p>
    </div>
    
    <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
      <div class="bg-gray-50 p-8 rounded-lg relative">
        <div class="relative z-10">
          <h2 class="text-2xl font-light mb-4">The Challenge</h2>
          <p class="mb-8 text-gray-700">
            Provide the public with accurate information about diseases so they can make informed decisions based on facts.
          </p>
        </div>
      </div>

      <div class="bg-gray-50 p-8 rounded-lg relative">
        <div class="relative z-10">
          <h2 class="text-2xl font-light mb-4">Value Delivered</h2>
          <p class="mb-8 text-gray-700">
            Enhanced health communication enabling governments to deliver essential information promptly and accurately.
          </p>
        </div>
      </div>
    </div>


    <!-- Full Width Image Section -->
    <div class="relative w-full overflow-hidden mt-16 mb-16 animate-on-view" data-delay="7">
      <img 
        src="https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fsystem.66df4020.png&w=3840&q=80" 
        alt="Pandemieradar Interface" 
        class="w-full h-full object-cover"
      />
      <div class="mt-8 border-t border-b border-black/10 py-6 text-center">
        <p class="text-sm text-gray-500">
          Real-time data dashboard combining epidemiological trends with social impact indicators in a modular design system.
        </p>
      </div>
    </div>

    <!-- Large Text Block after Challenge/Solution -->

    <section class="animate-on-view">
      <div class="container">
        <div class="grid">
   
      <p class="text-black/90 mb-16" style="line-height: 1.4;">
        The COVID-19 pandemic created a new awareness of health data in society. Never before have so many people engaged daily with different indicators, thresholds, and time series, reflecting both the need for information and security as well as the desire for participation and engagement. 
      </p>
      <p class="text-black/90 mb-16" style="line-height: 1.4;">
        Leveraging our expertise in human-centred design and interactive data visualization, we created a systematic solution that meets the needs of users across different demographics and empowers the general public to make informed decisions about their health, thereby contributing to society's overall well-being.
      </p>

      <div class="relative w-full overflow-hidden animate-on-view">
        <h2>Challenges</h2>
        <p>
          The primary challenge was to create a user-friendly dashboard that effectively caters complex health data to a broad and diverse audience. This involves underlying tasks such as:
        </p>
        
        <ul class="mt-4 list-disc list-inside">
          <li>Creating a variety of accessible data visualisations</li>
          <li>Providing comprehensible explanation texts</li>
          <li>Streamlining the navigation and optimising for mobile devices</li>
          <li>Ensuring web accessibility standards</li>
        </ul>
        
      </div>

</div>
</div>
    </section>

    <section class="animate-on-view mt-24">
    <!-- Key Features Section -->
    <div class="w-full mb-32">
      <h2 class="text-2xl font-light mt-8 mb-8 px-8">Key Features</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 px-8">
        <FeatureCell
          icon="CloudMonitoring"
          title="Symptom Monitoring"
          description="Tracks cough, fever, and sore throat trends in real time."
        />
        <FeatureCell
          icon="map"
          title="Regional Trends"
          description="Shows infection and symptom trends by region."
        />
        <FeatureCell
          icon="hospital"
          title="Hospitalization"
          description="Monitors hospital admissions and illness severity."
        />
        <FeatureCell
          icon="user"
          title="Data Quality"
          description="Displays participant numbers and data quality."
        />
      </div>
    </div>
  </section>


    
    <section class="animate-on-view">
      <div class="container">
        <div class="grid grid-cols-1 gap-4">
          <p>
            The introduction of <i>trend guides</i> per health indicator represents a pioneering effort in public health data dashboards, providing users with valuable context to interpret the data effectively.
          </p>
          <div class="relative w-full overflow-hidden mb-8">
            <img 
              src="https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Ftrend.ffe395f1.png&w=3840&q=80" 
              alt="Made to Measure visualization" 
              class="h-auto max-w-full animate-on-view" 
            />
            <div class="mt-8 border-t border-b border-black/10 py-6 text-center">
              <p class="text-sm text-gray-500">
                Interactive trend visualization showing infection rates and predictions
              </p>
            </div>
          </div>
          <!-- IMG -->
          <div class="grid grid-cols-1 gap-4">
            <div class="relative w-full overflow-hidden mb-8">
              <img 
                src="https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Ftimechart.d792a6d8.png&w=3840&q=75" 
                alt="Made to Measure visualization" 
                class="h-auto max-w-full animate-on-view" 
              />
              <div class="mt-8 border-t border-b border-black/10 py-6 text-center">
                <p class="text-sm text-gray-500">
                  An interactive time chart together with a short explanatory text help users to effortlessly comprehend the evolution of a health indicator over time.
                </p>
              </div>
            </div>

          <!-- IMG -->
          <div class="grid grid-cols-1 gap-4">
            <div class="relative w-full overflow-hidden mb-8">
              <img 
                src="https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fmap.6e1df534.png&w=3840&q=100" 
                alt="Made to Measure visualization" 
                class="h-auto max-w-full animate-on-view" 
              />
              <div class="mt-8 border-t border-b border-black/10 py-6 text-center">
                <p class="text-sm text-gray-500">
                  The map and the related table empower users to gain a deeper understanding of their local situation.
                </p>
              </div>
            </div>

            <!-- TEXT -->
          <div class="relative w-full overflow-hidden animate-on-view">
            <h2>Value</h2>
            <p>
              The data dashboard represents a groundbreaking endeavour in public health communication, addressing the critical need for accessible and comprehensive information, and fostering a more transparent and data-driven governance approach.
              Through continuous user testing and iterative refinement, we successfully tailored the Infektionsradar dashboard to meet the requirements of the German Federal Ministry of Health, the Robert Koch Institute and the users.
            </p>
          </div>
          <div class="mt-8 relative w-full overflow-hidden animate-on-view">
            <h2>Mobile First</h2>
            <p>
              The majority of users visit the dashboard via mobile devices. Responsive designs followed by device and user testings, ensure that the interactive dashboard is fully optimised for mobile usage.
            </p>
          </div>

            <div class="mt-8 relative w-full overflow-hidden">
            <img 
              src="https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fphone.b152c45e.jpg&w=3840&q=80" 
              alt="Made to Measure data visualization" 
              class="h-auto max-w-full animate-on-view" 
            />
          </div>
            <div class="mt-8 border-t border-b border-black/10 py-6 text-center">
              <p class="text-sm text-gray-500">
                Mobile interface showing real-time health data and statistics
              </p>
            </div>
          </div>
        </div>
    </section>
  </div>
  
  <!-- Credits Section -->
  <div class="animate-on-view" style="background-color: #f9fafb;">
    <div class="max-w-3xl mx-auto px-8 py-12 space-y-8">
      <div>
        <p class="text-sm text-gray-500 mb-2">Published by</p>
        <div class="flex flex-col items-start gap-1 mb-2">
          <span class="text-base font-light text-black/90">Federal Ministry of Health</span>
        </div>
      </div>
      <div>
        <p class="text-sm text-gray-500 mb-2">Commissioned by</p>
        <span class="text-base font-light text-black/90">Cosmonauts & Kings</span>
      </div>
      <div class="text-sm text-gray-500">
        This project was designed and developed by the team at Studio NAND. I 
        led the interaction design and prototyping process, working closely with developers and researchers.
      </div>
    </div>
  </div>

  <!-- Project Navigation -->
  <ProjectNav 
    prevProject={{ title: "Project Soli", url: "/projects/project-soli" }}
    nextProject={{ title: "DB Peak Spotting", url: "/projects/db-peak-spotting" }}
  />

  <Footer />
</div>
